'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AtSignIcon, KeyRoundIcon } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { getErrorMessage, getLocalizedRouteFromKeys } from '@/lib/utils';
import { EMAIL_REGEX } from '@/constants';
import { forgotPassword } from '@/lib/api';
import { ForgotPasswordParamsType } from '@/types/auth';
import { useAppLocale, useMetadata } from '@/hooks';
type PropsType = {
  className?: string;
};
const ForgotPasswordForm = t0 => {
  const $ = _c(73);
  const {
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t = useTranslations();
  const {
    backendLocale
  } = useAppLocale();
  const {
    routeParams
  } = useMetadata();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  let t2;
  if ($[0] !== backendLocale || $[1] !== routeParams) {
    t2 = getLocalizedRouteFromKeys("/login", backendLocale, routeParams);
    $[0] = backendLocale;
    $[1] = routeParams;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const loginRoute = t2;
  let t3;
  let t4;
  if ($[3] !== t) {
    const formSchema = z.object({
      email: z.string().trim().refine(_temp, t("invalid_email"))
    });
    t4 = useForm;
    t3 = zodResolver(formSchema);
    $[3] = t;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      email: ""
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3) {
    t6 = {
      resolver: t3,
      defaultValues: t5
    };
    $[7] = t3;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const form = t4(t6);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setSuccess(true);
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t) {
    t8 = error => {
      toast.error(getErrorMessage(error, t("password_reset_error")));
    };
    $[10] = t;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setLoading(false);
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8) {
    t10 = {
      mutationFn: _temp2,
      onSuccess: t7,
      onError: t8,
      onSettled: t9
    };
    $[13] = t8;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const {
    mutate
  } = useMutation(t10);
  let t11;
  if ($[15] !== mutate) {
    t11 = data_0 => {
      setLoading(true);
      mutate({
        email: data_0.email,
        source: "frontend"
      });
    };
    $[15] = mutate;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const onSubmit = t11;
  if (success) {
    let t12;
    if ($[17] !== t) {
      t12 = t("password_reset_sent");
      $[17] = t;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    let t13;
    if ($[19] !== t12) {
      t13 = <p className="text-sm text-green-800">{t12}</p>;
      $[19] = t12;
      $[20] = t13;
    } else {
      t13 = $[20];
    }
    let t14;
    if ($[21] !== t) {
      t14 = t("back_to_login");
      $[21] = t;
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    let t15;
    if ($[23] !== loginRoute || $[24] !== t14) {
      t15 = <Link href={loginRoute} className="text-brand text-sm font-medium hover:underline">{t14}</Link>;
      $[23] = loginRoute;
      $[24] = t14;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    let t16;
    if ($[26] !== t13 || $[27] !== t15) {
      t16 = <div className="flex flex-col items-center gap-4 rounded-md border border-green-200 bg-green-50 p-6 text-center">{t13}{t15}</div>;
      $[26] = t13;
      $[27] = t15;
      $[28] = t16;
    } else {
      t16 = $[28];
    }
    return t16;
  }
  const t12 = `flex w-full flex-col space-y-4 select-none ${className}`;
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <KeyRoundIcon className="h-5 w-5" />;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t("lost_password");
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14) {
    t15 = <h2 className="flex items-center gap-2 text-lg font-semibold uppercase">{t13}{t14}</h2>;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t) {
    t16 = t("enter_email_to_reset");
    $[34] = t;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t16) {
    t17 = <p className="text-muted-foreground text-sm">{t16}</p>;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t15 || $[39] !== t17) {
    t18 = <div className="flex flex-col gap-1 border-b pb-4">{t15}{t17}</div>;
    $[38] = t15;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== form || $[42] !== onSubmit) {
    t19 = form.handleSubmit(onSubmit);
    $[41] = form;
    $[42] = onSubmit;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== loading || $[45] !== t) {
    t20 = t21 => {
      const {
        field
      } = t21;
      return <FormItem><FormLabel required={true} htmlFor="forgot-email">{t("email")}</FormLabel><FormControl><InputGroup><InputGroupInput id="forgot-email" type="email" placeholder={t("enter_your_email")} {...field} autoFocus={true} disabled={loading} /><InputGroupAddon><AtSignIcon /></InputGroupAddon></InputGroup></FormControl><FormMessage /></FormItem>;
    };
    $[44] = loading;
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== form.control || $[48] !== t20) {
    t21 = <FormField control={form.control} name="email" render={t20} />;
    $[47] = form.control;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t) {
    t22 = t("reset_password");
    $[50] = t;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== loading || $[53] !== t22) {
    t23 = <Button type="submit" className="w-full uppercase" loading={loading} disabled={loading}>{t22}</Button>;
    $[52] = loading;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t19 || $[56] !== t21 || $[57] !== t23) {
    t24 = <form onSubmit={t19} className="space-y-4">{t21}{t23}</form>;
    $[55] = t19;
    $[56] = t21;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Separator />;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t) {
    t26 = t("back_to_login");
    $[60] = t;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== loginRoute || $[63] !== t26) {
    t27 = <div className="flex justify-center text-sm"><Link href={loginRoute} className="text-brand font-medium hover:underline">{t26}</Link></div>;
    $[62] = loginRoute;
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== t12 || $[66] !== t18 || $[67] !== t24 || $[68] !== t27) {
    t28 = <div className={t12}>{t18}{t24}{t25}{t27}</div>;
    $[65] = t12;
    $[66] = t18;
    $[67] = t24;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== form || $[71] !== t28) {
    t29 = <Form {...form}>{t28}</Form>;
    $[70] = form;
    $[71] = t28;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  return t29;
};
export default ForgotPasswordForm;
function _temp(val) {
  return EMAIL_REGEX.test(val);
}
async function _temp2(data) {
  return forgotPassword(data);
}